import { last, set } from 'lodash';
import type Handsontable from 'handsontable/base';
import type { CellValue } from 'hyperformula';

import type { CellData, CellSelection, CellValueChange, TableContentData } from '../../../../common/table/TableTypes';
import { createEmptyTableCellEntry } from '../../../../common/table/utils/tableInitialisationUtils';
import { getRawTextFromCellContentString } from '../../../../common/table/utils/tableCellContentStringUtils';
import { getColumnCount, getRowCount } from '../../../../common/table/utils/tableCellDataPropertyUtils';
import { isFormula } from '../utils/dataUtils/formula/tableFormulaPredicateUtils';
import { reorderCellSelection } from '../utils/selectionUtils/tableSelectionGeometryUtils';
import { MNHotInstance } from '../tableHotInstanceTypes';

type SourceCellData = {
    renderCellValue: string;
    cellData: CellData;
};
type SourceCellDataArray = SourceCellData[][];
type HotTableCellMeta = {
    cellData?: CellData;
};

/**
 * Shared clipboard source state for Milanote table copy, paste, and autofill flows.
 */
export class HotTableClipboardSourceManager {
    private copySource: SourceCellDataArray | undefined;
    private autofillSource: SourceCellDataArray | undefined;

    /**
     * Stores the latest copied table selection from a Handsontable instance.
     */
    setTableCopySource(hotInstance: MNHotInstance, cellSelection: CellSelection): void {
        this.copySource = this.getSourceCellDataArray(hotInstance, cellSelection);
    }

    /**
     * Stores the autofill source selection from a Handsontable instance.
     */
    setTableAutofillSource(hotInstance: MNHotInstance, cellSelection: CellSelection): void {
        this.autofillSource = this.getSourceCellDataArray(hotInstance, cellSelection);
    }

    /**
     * Returns copied Milanote cell data only when the pasted raw values still match the last copied range.
     */
    getTableCopiedDataForValues(cellValues: CellValue[][]): TableContentData | undefined {
        if (!this.copySource) return;

        const nRowsOfValues = getRowCount(cellValues);
        const nColsOfValues = getColumnCount(cellValues);
        const nRowsOfCopySource = getRowCount(this.copySource);
        const nColsOfCopySource = getColumnCount(this.copySource);

        const isValidCopy =
            nRowsOfCopySource === nRowsOfValues &&
            nColsOfCopySource === nColsOfValues &&
            this.copySource.every((rowData, row) =>
                rowData.every(
                    ({ renderCellValue }, col) =>
                        renderCellValue === this.getRawTextFromCopiedValue(cellValues[row][col]),
                ),
            );

        if (!isValidCopy) return;

        return this.copySource.map((rowData) =>
            rowData.map(({ renderCellValue, cellData }) => {
                if (isFormula(cellData.value)) return { ...cellData, value: renderCellValue };

                return cellData;
            }),
        );
    }

    /**
     * Rebuilds copied Milanote cell data from HOT change tuples.
     */
    getTableCopiedDataForChanges(changes: CellValueChange[]): TableContentData | undefined {
        if (!changes.length) return;

        const minChangeRow = Math.min(...changes.map(([row]) => row));
        const minChangeCol = Math.min(...changes.map(([, col]) => Number(col)));

        const cellValues: CellValue[][] = [];

        changes.forEach(([row, col, , value]) =>
            set(cellValues, [row - minChangeRow, Number(col) - minChangeCol], value),
        );

        return this.getTableCopiedDataForValues(cellValues);
    }

    /**
     * Returns the last captured autofill cell data payload.
     */
    getTableAutofillData(): TableContentData | undefined {
        return this.autofillSource?.map((rowData) => rowData.map(({ cellData }) => cellData));
    }

    /**
     * Reads and stores the source cell payloads for a selection from the current HOT instance.
     */
    private getSourceCellDataArray(hotInstance: MNHotInstance, cellSelection: CellSelection): SourceCellDataArray {
        const [fromRow, fromCol, toRow, toCol] = reorderCellSelection(cellSelection);

        const sourceCellDataArray: SourceCellDataArray = [];
        for (let row = fromRow; row <= toRow; row++) {
            sourceCellDataArray.push([]);
            for (let col = fromCol; col <= toCol; col++) {
                const { cellData = createEmptyTableCellEntry() } = hotInstance.getCellMeta(
                    row,
                    col,
                ) as HotTableCellMeta;
                const renderCellValue = this.normalizeCopiedSourceText(hotInstance.getCopyableData(row, col));

                last(sourceCellDataArray)?.push({ renderCellValue, cellData });
            }
        }

        return sourceCellDataArray;
    }

    /**
     * Handsontable can normalize non-breaking spaces in pasted tables into regular spaces.
     * Normalize copied source text up front so strict paste matching still succeeds.
     */
    private normalizeCopiedSourceText(value: Handsontable.CellValue): string {
        return `${value || ''}`.replace(/\u00A0/g, ' ');
    }

    /**
     * Returns a stable comparison value for pasted content, including empty cells.
     */
    private getRawTextFromCopiedValue(value: Handsontable.CellValue): string | number {
        if (value == null) return '';

        return getRawTextFromCellContentString(value);
    }
}

export default new HotTableClipboardSourceManager();
